import React, { useState } from 'react';
import {
  Box, Button, FormControl, IconButton, MenuItem, Modal, Select, Typography
} from '@mui/material';
import { IoMdClose } from 'react-icons/io';
import { FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';

const UPLOAD_TYPES = {
  EMPLOYEE_MASTER: 'employee_master',
  EMPLOYEE_WAGE: 'employee_wage',
  EMPLOYEE_ATTENDANCE: 'employee_attendance',
  EMPLOYEE_LEAVE_CREDIT: 'employee_leave_credit',
  EMPLOYEE_LEAVE_AVAILED: 'employee_leave_availed'
};

const UPLOAD_TYPE_LABELS: any = {
  [UPLOAD_TYPES.EMPLOYEE_MASTER]: 'Employee Master',
  [UPLOAD_TYPES.EMPLOYEE_WAGE]: 'Employee Wage',
  [UPLOAD_TYPES.EMPLOYEE_ATTENDANCE]: 'Employee Attendance',
  [UPLOAD_TYPES.EMPLOYEE_LEAVE_CREDIT]: 'Employee Leave Credit',
  [UPLOAD_TYPES.EMPLOYEE_LEAVE_AVAILED]: 'Employee Leave Availed'
};

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  uploadType: string;
  onUpload: (file: File, selectedData: any) => void;
  companies: any[];
  associateCompanies: any[];
  locations: any[];
  onDownloadFormat?: () => void;
  establishmentType?: string;
}

const styleUploadModal = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
};

const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  uploadType,
  onUpload,
  companies,
  associateCompanies,
  locations,
  onDownloadFormat,
  establishmentType = ''
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedAssociateCompany, setSelectedAssociateCompany] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File size should not exceed ${MAX_FILE_SIZE_MB}MB`);
        setSelectedFile(null);
        event.target.value = null;
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    if (!selectedAssociateCompany) {
      toast.error('Please select an associate company');
      return;
    }

    if (!selectedLocation) {
      toast.error('Please select a location');
      return;
    }

    const selectedData = {
      company: selectedCompany,
      associateCompany: selectedAssociateCompany,
      location: selectedLocation,
      establishmentType: establishmentType
    };

    onUpload(selectedFile, selectedData);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSelectedCompany('');
    setSelectedAssociateCompany('');
    setSelectedLocation('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={styleUploadModal}>
        <Box sx={{
          backgroundColor: '#E2E3F8',
          padding: '10px',
          px: '20px',
          borderRadius: '6px',
          boxShadow: '0px 6px 10px #CDD2D9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography sx={{ font: 'normal normal normal 32px/40px Calibri' }}>
            Upload {UPLOAD_TYPE_LABELS[uploadType] || 'Data'}
          </Typography>
          <IconButton onClick={handleClose}>
            <IoMdClose />
          </IconButton>
        </Box>

        <Box sx={{ padding: '20px', backgroundColor: '#ffffff' }}>
          {/* Company Selection */}
          <Box sx={{ mb: 2 }}>
            <Typography mb={1}>Company</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Company
                </MenuItem>
                {companies.map((company: any) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Associate Company Selection */}
          <Box sx={{ mb: 2 }}>
            <Typography mb={1}>Associate Company</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedAssociateCompany}
                onChange={(e) => setSelectedAssociateCompany(e.target.value)}
                disabled={!selectedCompany}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Associate Company
                </MenuItem>
                {associateCompanies.map((company: any) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Location Selection */}
          <Box sx={{ mb: 2 }}>
            <Typography mb={1}>Location</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                disabled={!selectedAssociateCompany}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Location
                </MenuItem>
                {locations && locations.length > 0 ? (
                  locations.map((each: any) => {
                    const { name, code, city }: any = each.location || {};
                    const { state } = city || {};

                    if (!each.location_id || !state) {
                      return null;
                    }

                    return (
                      <MenuItem key={each.location_id} value={each.location_id + '^' + state.id}>
                        {`${name} (${state.code}-${city.code}-${code})`}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem disabled value="">
                    Select Location
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>

          {/* Download Format Button */}
          {onDownloadFormat && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FaDownload />}
                onClick={onDownloadFormat}
                fullWidth
              >
                Download Format
              </Button>
            </Box>
          )}

          {/* File Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography mb={1}>Select File (Max {MAX_FILE_SIZE_MB}MB)</Typography>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={{ width: '100%' }}
            />
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)}MB)
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
            <Button variant='outlined' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='contained' onClick={handleUpload}>
              Upload
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadModal;
export { UPLOAD_TYPES };
